/**
 * IndexedDB Files Storage Layer
 * Provides a thin wrapper around IndexedDB for storing and retrieving
 * the library files list and associated metadata.
 *
 * Public API (ES module):
 * - isIdbSupported()
 * - isIdbEnabled()
 * - openDatabase()
 * - closeDatabase(db)
 * - getMeta(db)
 * - setMeta(db, meta)
 * - clearFiles(db)
 * - bulkUpsertFiles(db, rawFiles)
 * - getAllFiles(db)
 * - getFilesSnapshot(db, limit)
 *
 * Data model:
 * DB: libraryFiles
 * Stores:
 *  - meta (keyPath: 'key') where key='state' → { etag, lastModified, version, count, builtAt }
 *  - bulk (keyPath: 'key') → single record `{ key:'files_blob', payload: [...] }`
 */

const DB_NAME = 'libraryFiles';
const DB_VERSION = 8; // v8: invalidate old payloads; ensure s is treated as KB consistently
const STORE_META = 'meta';


export function isIdbSupported() {
  try {
    return typeof window !== 'undefined' && !!window.indexedDB;
  } catch (_) {
    return false;
  }
}

export function isIdbEnabled() {
  // Always enabled by default
  return true;
}

function debugLog(event, data) {
  try {
    if (window.HyperLog && typeof window.HyperLog.debug === 'function' && debugMode === '1') {
      window.HyperLog.debug(`[IDB] ${event}`, data);
    }
  } catch (_) { /* noop */ }
}

export function openDatabase() {
  return new Promise((resolve, reject) => {
    if (!isIdbSupported()) {
      return reject(new Error('IndexedDB not supported'));
    }

    const request = window.indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = (event) => {
      const db = request.result;
      // Drop legacy 'files' store if present (we only use 'bulk')
      try { if (db.objectStoreNames.contains('files')) db.deleteObjectStore('files'); } catch(_) { /* noop */ }
      // Create stores if they don't exist yet
      if (!db.objectStoreNames.contains(STORE_META)) {
        db.createObjectStore(STORE_META, { keyPath: 'key' });
      }
      // New in v2: bulk store for single-record storage of entire list
      if (!db.objectStoreNames.contains('bulk')) {
        db.createObjectStore('bulk', { keyPath: 'key' });
      }
      // v6: recreate summaries WITHOUT keyPath (store values as raw strings)
      try { if (db.objectStoreNames.contains('summaries')) db.deleteObjectStore('summaries'); } catch(_) { /* noop */ }
      db.createObjectStore('summaries');
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error('Failed to open IndexedDB'));
  });
}

export function closeDatabase(db) {
  try { db && db.close && db.close(); } catch (_) { /* noop */ }
}

export function getMeta(db) {
  return new Promise((resolve, reject) => {
    try {
      const tx = db.transaction(STORE_META, 'readonly');
      const store = tx.objectStore(STORE_META);
      const req = store.get('files_state');
      req.onsuccess = () => resolve(req.result || null);
      req.onerror = () => reject(req.error || new Error('Failed to read meta'));
    } catch (e) {
      reject(e);
    }
  });
}

export function setMeta(db, meta) {
  const value = {
    key: 'files_state',
    ...meta,
    updatedAt: new Date().toISOString()
  };
  return new Promise((resolve, reject) => {
    try {
      const tx = db.transaction(STORE_META, 'readwrite');
      const store = tx.objectStore(STORE_META);
      const req = store.put(value);
      req.onsuccess = () => resolve(true);
      req.onerror = () => reject(req.error || new Error('Failed to write meta'));
    } catch (e) {
      reject(e);
    }
  });
}

// Convenience accessor for global usage without opening DB (requires FilesCacheManager to cache last meta)
if (typeof window !== 'undefined') {
  window.__idbMetaCache = window.__idbMetaCache || null;
}

export function clearFiles(db) {
  // Clear bulk payload (primary) and attempt to clear legacy 'files' store if present
  return new Promise((resolve, reject) => {
    try {
      const stores = db.objectStoreNames.contains('files') ? ['bulk', 'files'] : ['bulk'];
      const tx = db.transaction(stores, 'readwrite');
      let pending = stores.length;
      stores.forEach((name) => {
        try {
          const store = tx.objectStore(name);
          const req = store.clear();
          req.onsuccess = () => { if (--pending === 0) resolve(true); };
          req.onerror = () => reject(req.error || new Error(`Failed to clear store ${name}`));
        } catch (e) {
          // If store missing, decrement pending
          if (--pending === 0) resolve(true);
        }
      });
      tx.onerror = () => {/* individual req handlers already handle errors */};
      tx.onabort = () => {/* noop */};
    } catch (e) {
      reject(e);
    }
  });
}

function deriveRecord(raw) {
  // Normalize record; 's' must be in KB for UI consistency
  const name = raw.n || raw.name || '';
  const path = raw.p || raw.path || '';
  // Prefer short key 's' (KB). If only 'size' (bytes) provided, convert to KB.
  const sizeKb = typeof raw.s === 'number'
    ? raw.s
    : (typeof raw.size === 'number' ? Math.round(raw.size / 1000) : 0);
  const id = `${path}/${name}`;
  const lower = name.toLowerCase();
  const ext = name.includes('.') ? name.split('.').pop().toLowerCase() : '';
  return {
    id,
    n: name,
    p: path,
    s: sizeKb,
    nLower: lower,
    ext,
    d: (typeof raw.d === 'string') ? raw.d : ''
  };
}

export async function bulkUpsertFiles(db, rawFiles) {
  if (!Array.isArray(rawFiles) || rawFiles.length === 0) return 0;
  // Store entire array as a single blob in 'bulk' store (primary and only path)
  const t0 = (typeof performance !== 'undefined' ? performance.now() : Date.now());
  const txBulk = db.transaction('bulk', 'readwrite');
  const bulk = txBulk.objectStore('bulk');
  await new Promise((resolve, reject) => {
    const req = bulk.put({ key: 'files_blob', payload: rawFiles });
    req.onsuccess = () => resolve(true);
    req.onerror = () => reject(req.error || new Error('Failed to write bulk payload'));
  });
  const bulkMs = (typeof performance !== 'undefined' ? performance.now() : Date.now()) - t0;
  try { if (window.mainApp && typeof window.mainApp.updateLoaderProgress === 'function') { window.mainApp.updateLoaderProgress(null, 'Saving files…'); } } catch(_) { /* noop */ }
  return rawFiles.length;
}

export function getAllFiles(db) {
  // Prefer bulk blob if present (fast single read), fallback to indexed store
  return new Promise((resolve, reject) => {
    try {
      const txBulk = db.transaction('bulk', 'readonly');
      const bulk = txBulk.objectStore('bulk');
      const bulkReq = bulk.get('files_blob');
      bulkReq.onsuccess = () => {
        if (bulkReq.result && Array.isArray(bulkReq.result.payload)) {
          const payload = bulkReq.result.payload;
          resolve(payload);
        } else {
          resolve([]);
        }
      };
      bulkReq.onerror = () => {
        resolve([]);
      };
    } catch (e) {
      reject(e);
    }
  });
}

export function getFilesSnapshot(db, limit = 200) {
  return new Promise((resolve, reject) => {
    try {
      // Snapshot from bulk: slice first N entries
      const tx = db.transaction('bulk', 'readonly');
      const store = tx.objectStore('bulk');
      const req = store.get('files_blob');
      req.onsuccess = () => {
        const payload = (req.result && Array.isArray(req.result.payload)) ? req.result.payload : [];
        resolve(payload.slice(0, Math.max(0, limit)));
      };
      req.onerror = () => reject(req.error || new Error('Failed to read snapshot'));
    } catch (e) {
      reject(e);
    }
  });
}

// ===== Summaries API (per-key) =====

/**
 * Get summary meta from meta store: key 'summaries_state'
 */
export function getSummariesMeta(db) {
  return new Promise((resolve, reject) => {
    try {
      const tx = db.transaction(STORE_META, 'readonly');
      const store = tx.objectStore(STORE_META);
      const req = store.get('summaries_state');
      req.onsuccess = () => resolve(req.result || null);
      req.onerror = () => reject(req.error || new Error('Failed to read summaries meta'));
    } catch (e) { reject(e); }
  });
}

/**
 * Set summaries meta in meta store
 */
export function setSummariesMeta(db, meta) {
  const value = { key: 'summaries_state', ...meta, updatedAt: new Date().toISOString() };
  return new Promise((resolve, reject) => {
    try {
      const tx = db.transaction(STORE_META, 'readwrite');
      const store = tx.objectStore(STORE_META);
      const req = store.put(value);
      req.onsuccess = () => resolve(true);
      req.onerror = () => reject(req.error || new Error('Failed to write summaries meta'));
    } catch (e) { reject(e); }
  });
}

/**
 * Bulk upsert summaries items: items is array of { k: key, v: value }
 */
export async function bulkUpsertSummaries(db, items) {
  if (!Array.isArray(items) || items.length === 0) return 0;
  const t0 = (typeof performance !== 'undefined' ? performance.now() : Date.now());
  const tx = db.transaction('summaries', 'readwrite');
  const store = tx.objectStore('summaries');
  await new Promise((resolve, reject) => {
    let pending = items.length;
    items.forEach((it) => {
      try {
        const key = it.key || it.k;
        const rawValue = it.value || it.v;
        // Only store the 'summary' string, nothing else
        const summary = (typeof rawValue === 'string') ? rawValue : (rawValue && typeof rawValue.summary === 'string' ? rawValue.summary : '');
        const req = store.put(summary, key);
        req.onsuccess = () => { if (--pending === 0) resolve(true); };
        req.onerror = () => reject(req.error || new Error('Failed to write summary record'));
      } catch (e) {
        reject(e);
      }
    });
  });
  const ms = (typeof performance !== 'undefined' ? performance.now() : Date.now()) - t0;
  return items.length;
}

/**
 * Get many summaries by keys.
 */
export function getSummariesByKeys(db, keys) {
  return new Promise((resolve, reject) => {
    try {
      if (!Array.isArray(keys) || keys.length === 0) return resolve([]);
      const tx = db.transaction('summaries', 'readonly');
      const store = tx.objectStore('summaries');
      const results = new Array(keys.length);
      let remaining = keys.length;
      keys.forEach((k, idx) => {
        const req = store.get(k);
        req.onsuccess = () => {
          results[idx] = (typeof req.result === 'string') ? req.result : undefined;
          if (--remaining === 0) resolve(results);
        };
        req.onerror = () => {
          results[idx] = undefined;
          if (--remaining === 0) resolve(results);
        };
      });
    } catch (e) { reject(e); }
  });
}

/**
 * Clear summaries store entirely
 */
export function clearSummaries(db) {
  return new Promise((resolve, reject) => {
    try {
      const tx = db.transaction('summaries', 'readwrite');
      const store = tx.objectStore('summaries');
      const req = store.clear();
      req.onsuccess = () => resolve(true);
      req.onerror = () => reject(req.error || new Error('Failed to clear summaries store'));
    } catch (e) { reject(e); }
  });
}

/**
 * Get count of records in summaries store
 */
export function getSummariesCount(db) {
  return new Promise((resolve, reject) => {
    try {
      const tx = db.transaction('summaries', 'readonly');
      const store = tx.objectStore('summaries');
      const req = store.count();
      req.onsuccess = () => resolve(req.result || 0);
      req.onerror = () => reject(req.error || new Error('Failed to count summaries'));
    } catch (e) { reject(e); }
  });
}


